/*
 * <cover-selector> — pokrowce swatch picker with a shared video-compare modal.
 *
 * - Swatch buttons drive the (visually hidden) native radio inputs, so the
 *   theme's existing <variant-options> component resolves the variant, price,
 *   gallery and URL exactly as it does for any other option.
 * - One modal per product. Thumbs inside the modal switch the previewed film
 *   and description without closing (comparison). "Wybierz ten pokrowiec"
 *   commits the previewed cover as the selected variant.
 * - Video is lazy: <video> elements are created only when the modal opens and
 *   paused/removed on close so nothing plays in the background.
 */
if (!customElements.get("cover-selector")) {
  customElements.define(
    "cover-selector",
    class CoverSelector extends HTMLElement {
      connectedCallback() {
        this.swatches = Array.from(this.querySelectorAll("[data-cover-swatch]"));
        this.modal = this.querySelector("[data-cover-modal]");
        this.compareBtn = this.querySelector("[data-cover-compare]");
        this.opener = null;
        this.previewIndex = 0;

        // Card swatch: selects the variant. Clicking the ▶ play badge on a
        // swatch that has a film opens the compare modal at that cover instead.
        this.swatches.forEach((btn) => {
          btn.addEventListener("click", (e) => {
            if (this.modal && e.target.closest(".wt-cover__swatch__play")) {
              this.openModal(Number(btn.dataset.index), btn);
              return;
            }
            this.selectCover(Number(btn.dataset.index));
          });
        });

        if (this.compareBtn) {
          this.compareBtn.addEventListener("click", () => {
            this.openModal(this.getSelectedIndex(), this.compareBtn);
          });
        }

        if (this.modal) {
          this.setupModal();
          // Portal the modal to <body> so position:fixed is relative to the
          // viewport (escapes transformed ancestors) and it stacks above the
          // header instead of being clipped/covered by it.
          document.body.appendChild(this.modal);
        }

        // Keep swatch state in sync when the variant changes anywhere else
        // (availability, restored URL variant, etc.).
        const section = this.closest("section") || document;
        section.addEventListener("variantChangeEnd", () => this.syncFromRadios());

        this.syncFromRadios();
      }

      disconnectedCallback() {
        // Remove the portaled modal and any lingering global state so a
        // re-rendered instance (e.g. high-variant fetch) doesn't leave orphans.
        document.removeEventListener("keydown", this.onKeydown);
        document.body.classList.remove("wt-cover-modal-open");
        if (this.modal && this.modal.parentNode === document.body) {
          this.modal.remove();
        }
      }

      /* ---------- radios <-> swatches ---------- */

      radioFor(index) {
        const btn = this.swatches[index];
        return btn ? document.getElementById(btn.dataset.radioId) : null;
      }

      getSelectedIndex() {
        const i = this.swatches.findIndex((btn) => {
          const radio = document.getElementById(btn.dataset.radioId);
          return radio && radio.checked;
        });
        return i === -1 ? 0 : i;
      }

      selectCover(index) {
        const radio = this.radioFor(index);
        if (!radio || radio.checked) {
          this.syncFromRadios();
          return;
        }
        radio.checked = true;
        // <variant-options> listens for a bubbling change event.
        radio.dispatchEvent(new Event("change", { bubbles: true }));
        this.syncFromRadios();
      }

      syncFromRadios() {
        this.swatches.forEach((btn) => {
          const radio = document.getElementById(btn.dataset.radioId);
          const selected = !!(radio && radio.checked);
          btn.classList.toggle("is-selected", selected);
          btn.setAttribute("aria-pressed", selected ? "true" : "false");
          const unavailable = !!(radio && radio.classList.contains("disabled"));
          btn.toggleAttribute("aria-disabled", unavailable);
        });
      }

      /* ---------- modal ---------- */

      setupModal() {
        this.thumbs = Array.from(this.modal.querySelectorAll("[data-cover-thumb]"));
        this.infoItems = Array.from(this.modal.querySelectorAll("[data-cover-info]"));
        this.videoBox = this.modal.querySelector("[data-cover-video]");
        this.selectBtn = this.modal.querySelector("[data-cover-select]");

        this.modal.querySelectorAll("[data-cover-close]").forEach((el) => {
          el.addEventListener("click", () => this.closeModal());
        });

        this.thumbs.forEach((thumb) => {
          thumb.addEventListener("click", () => {
            this.previewCover(Number(thumb.dataset.index));
          });
        });

        if (this.selectBtn) {
          this.selectBtn.addEventListener("click", () => {
            this.selectCover(this.previewIndex);
            this.closeModal();
          });
        }

        this.onKeydown = (e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            this.closeModal();
          } else if (e.key === "Tab") {
            this.trapFocus(e);
          }
        };
      }

      openModal(index, opener) {
        if (!this.modal) return;
        this.opener = opener || document.activeElement;
        this.modal.hidden = false;
        document.body.classList.add("wt-cover-modal-open");
        document.addEventListener("keydown", this.onKeydown);
        this.previewCover(index, true);
        // Move focus into the dialog.
        const first = this.modal.querySelector("[data-cover-close]");
        if (first) first.focus();
      }

      closeModal() {
        if (!this.modal || this.modal.hidden) return;
        this.modal.hidden = true;
        document.body.classList.remove("wt-cover-modal-open");
        document.removeEventListener("keydown", this.onKeydown);
        this.stopVideo();
        if (this.opener && typeof this.opener.focus === "function") {
          this.opener.focus();
        }
      }

      previewCover(index, force) {
        if (index === this.previewIndex && !force) return;
        this.previewIndex = index;

        this.thumbs.forEach((t) => {
          t.classList.toggle("is-active", Number(t.dataset.index) === index);
        });
        this.infoItems.forEach((item) => {
          item.hidden = Number(item.dataset.index) !== index;
        });
        this.loadVideo(index);
      }

      loadVideo(index) {
        if (!this.videoBox) return;
        const thumb = this.thumbs.find((t) => Number(t.dataset.index) === index);
        const src = thumb ? thumb.dataset.video : "";

        this.stopVideo();
        this.videoBox.innerHTML = "";
        this.videoBox.classList.remove("wt-cover-modal__video--empty");

        if (!src) {
          this.videoBox.classList.add("wt-cover-modal__video--empty");
          this.videoBox.textContent = this.modal.dataset.emptyLabel || "";
          return;
        }

        const video = document.createElement("video");
        video.src = src;
        video.muted = true;
        video.loop = true;
        video.autoplay = true;
        video.playsInline = true;
        video.controls = true;
        video.setAttribute("playsinline", "");
        video.preload = "auto";
        this.video = video;
        this.videoBox.appendChild(video);
        const p = video.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      }

      stopVideo() {
        if (this.video) {
          this.video.pause();
          this.video.removeAttribute("src");
          this.video.load();
          this.video = null;
        }
      }

      trapFocus(e) {
        const focusable = this.modal.querySelectorAll(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
        );
        const visible = Array.from(focusable).filter(
          (el) => el.offsetParent !== null || el === document.activeElement,
        );
        if (!visible.length) return;
        const first = visible[0];
        const last = visible[visible.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
  );
}
